import React, { useEffect, useState } from 'react';
import { authHeaders } from '../../services/auth';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ShieldCheck, Save, Loader2, Mail, Phone, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const SAGrievanceDefaultPanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [contact, setContact] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/tc/super-admin/grievance-contact-default`, {
        headers: authHeaders(), credentials: 'include',
      });
      if (res.ok) {
        const j = await res.json();
        setContact(j.contact);
        setForm({ name: j.contact?.name || '', email: j.contact?.email || '', phone: j.contact?.phone || '' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('All fields required'); return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/tc/super-admin/grievance-contact-default`, {
        method: 'PUT',
        credentials: 'include',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); toast.error(j.detail || 'Save failed'); return; }
      toast.success('Global grievance default updated');
      setEditing(false);
      await load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-6 mt-6"
      data-testid="sa-grievance-default-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-700" />
          <h3 className="text-lg font-semibold text-slate-900">Global Grievance & Privacy Default</h3>
        </div>
        {!editing && !loading && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="sa-grievance-edit-button">
            Edit
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Shown to residents in the T&C if their community has not configured a community-specific contact.
      </p>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : !editing ? (
        <div className="space-y-1.5 text-sm" data-testid="sa-grievance-display">
          <div className="flex items-center gap-2"><UserIcon className="w-4 h-4 text-slate-400" /> {contact?.name}</div>
          <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /> {contact?.email}</div>
          <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /> {contact?.phone}</div>
        </div>
      ) : (
        <div className="space-y-3 max-w-md">
          <div>
            <Label className="text-xs">Name</Label>
            <Input data-testid="sa-grievance-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input data-testid="sa-grievance-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Phone</Label>
            <Input data-testid="sa-grievance-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={save} disabled={saving} data-testid="sa-grievance-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SAGrievanceDefaultPanel;
